import { Component, OnInit } from '@angular/core';
import { GeneralExerciseComponent } from '../../General/g-exercise.component';
import { ActivatedRoute } from '@angular/router';
import { TopicService } from '../topic.service';
import { MessageService } from '../../shared/message.service';
import { SelfcorrectingService } from '../selfcorrecting.service';
import * as shape from 'd3-shape';

@Component({
  selector: 'app-create-codes',
  templateUrl: './create-codes.component.html',
  styleUrls: ['./create-codes.component.css']
})
export class CreateCodesComponent extends GeneralExerciseComponent {

  constructor(activatedRoute: ActivatedRoute, topicService: TopicService, messageService: MessageService, protected selfcorrectingService: SelfcorrectingService) {
    super(activatedRoute, topicService, messageService)
  }

  lengthOfCodes: number;
  numberOfCodes: number;
  minimalDistance: number;
  values: string[] = [];
  givenCodes: string[] = [];
  codes: string[] = [];
  modus: string;
  showCube: boolean;

  noCodes: string[] = [];


  view: any[] = [600, 300];
  fitContainer: boolean = true;
  curve = shape.curveBundle;
  nodes: any[] = [];
  edges: any[] = [];

  errors: number[] = [];

  get codeLength(): number {
    return this.codes.length;
  }

  setTypeSpecificsFromConfiguration() {
    this.lengthOfCodes = this.configuration.typespecifics.lengthOfCodes;
    this.numberOfCodes = this.configuration.typespecifics.numberOfCodes;
    this.minimalDistance = this.configuration.typespecifics.minimalDistance;
    this.givenCodes = this.configuration.typespecifics.givenCodes;
    this.values = this.configuration.typespecifics.values;
    this.modus = this.configuration.typespecifics.modus;
    this.showCube = this.configuration.typespecifics.showCube;

    this.codes = [...this.givenCodes];

    if (this.numberOfCodes == undefined && !this.showCube) {
      this.codes = ["", ""];
    }

    this.computeGraph();

    this.printable = this.showCube && this.lengthOfCodes < 6;

  }

  setTypeSpecificsFromState() {
    if (this.state.typespecific) {
      if (this.state.typespecific.codes) {
        this.codes = this.state.typespecific.codes;
      }
    }
    let temp = this.codes.filter(v => v != null && v != undefined && v != "").map(v => this.selfcorrectingService.distanceSmallerEqual(v.length, this.minimalDistance - 1, v));
    this.noCodes = [].concat(...temp);

    this.computeGraph();
  }

  computeState() {
    this.state.typespecific.codes = this.codes;
  }

  checkSolution() {

    if (this.codes.length != this.numberOfCodes) {
      this.messageService.addAlertWithTimeout(this.get("wrongNumberofCodes"), "danger", 10000);
      return;
    }
    this.errors = this.codes.map((v, i) => [v, i]).filter(v => v[0].toString().length != this.lengthOfCodes && this.lengthOfCodes != undefined).map(v => +v[1]);
    if (this.errors.length > 0) {
      this.messageService.addAlertWithTimeout(this.get("wrongLengthOfCodes"), "danger", 10000);
      return;
    }
    if (this.selfcorrectingService.minDistance(this.codes) < this.minimalDistance) {
      let pairs = [];
      for (let i = 0; i < this.codes.length; i++) {
        for (let j = 0; j < i; j++) {
          pairs.push([this.codes[i], this.codes[j]]);
        }
      }
      let wrongPairs = pairs.map(v => [v[0], v[1], this.selfcorrectingService.distance(v[0], v[1])]).filter(v => v[2] < this.minimalDistance).map(v => [v[0], v[1]]);
      this.messageService.addAlertWithTimeout(this.get("tooLargeMinimalDistance").concat(": ", wrongPairs.map(v => "[".concat(v[0].toString(), ",", v[1].toString(), "]")).join("")), "danger", 10000);
      return;
    }
    this.messageService.addAlertWithTimeout(this.get("correct"), "success", 10000);
    this.state.solved = true;
    this.solved = true;
    this.saveState();
  }

  evaluate(): number[] {
    if (this.selfcorrectingService.minDistance(this.codes) >= this.minimalDistance) {
      return [2 * (this.codes.filter(v => v != undefined && v.length == this.lengthOfCodes).length / this.numberOfCodes), 2];
    }
    return [0, 2];
  }


  /** compute the supporting graph */
  computeGraph() {
    if (this.lengthOfCodes < 6 && this.lengthOfCodes > 1) {
      this.nodes = [];
      this.edges = [];
      let nodeList = this.selfcorrectingService.allCodes(this.lengthOfCodes);
      for (let n of nodeList) {
        this.nodes.push({
          id: n,
          label: n
        });
      }

      for (let i = 0; i < nodeList.length; i++) {
        for (let j = 0; j < i; j++) {
          if (this.selfcorrectingService.distance(nodeList[i], nodeList[j]) == 1) {
            this.edges.push({
              source: nodeList[i],
              target: nodeList[j],
              label: ''
            })
          }
        }
      }
    } else /*if (this.codes.length > 0)*/ {
      this.nodes = [];
      this.edges = [];
      for (let n of this.codes) {
        this.nodes.push({
          id: n,
          label: n
        });
      }

      for (let i = 0; i < this.codes.length; i++) {
        for (let j = 0; j < i; j++) {
          this.edges.push({
            source: this.codes[i],
            target: this.codes[j],
            label: this.selfcorrectingService.distance(this.codes[i], this.codes[j])
          })
        }
      }
    }
  }

  /** normalize the codes */
  normalizeCode(code: string) {
    if (code != undefined) {
      return code.replace(/[^01]/, "");
    }
  }

  /** add the selected label 
   * @param label - code to add
  */
  selectNode(label: string) {
    if (this.noCodes.includes(label) || this.codes.includes(label)) {
      return;
    }
    let i = 0;
    while (i < this.numberOfCodes) {
      if (this.codes[i] == "" || this.codes[i] == undefined) {
        this.codes[i] = label;
        break;
      }
      i++;
    }
    this.noCodes.push(...this.selfcorrectingService.distanceSmallerEqual(label.length, this.minimalDistance - 1, label));

  }

  /** delelte the i-th label
   * @param i - index of code to delete
   */
  deleteCode(i: number) {
    this.codes[i] = "";
    let temp = this.codes.filter(v => v != null && v != undefined && v != "").map(v => this.selfcorrectingService.distanceSmallerEqual(v.length, this.minimalDistance - 1, v));
    this.noCodes = [].concat(...temp);
  }

  /** recompute the graph and the noCodes */
  changeCode(i: number) {
    if (this.codes[i].length == this.lengthOfCodes) {
      let temp = this.codes.filter(v => v != null && v != undefined && v != "").map(v => this.selfcorrectingService.distanceSmallerEqual(v.length, this.minimalDistance - 1, v));
      this.noCodes = [].concat(...temp);
    } else if (this.codes[i] == null || this.codes[i] == undefined || this.codes[i].length == 0) {
      this.deleteCode(i);
    }
    this.computeGraph();

  }


}
